import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Megaphone, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const AUD = { all: "Everyone", free: "Free accounts", pro: "Pro subscribers", trial: "Trial users", test: "Single test address" };

const History = () => {
  const { data = [] } = useQuery({
    queryKey: ["admin-announcements"], queryFn: () => api.get("/admin/announcements").then((r) => r.data),
    refetchInterval: (q) => (q.state.data || []).some((a) => a.status === "sending") ? 3000 : false,
  });
  if (!data.length) return null;
  return (
    <div className="mt-6 border-t border-[#2D3442] pt-4" data-testid="announce-history">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">Sent</div>
      {data.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`announce-row-${a.id}`}>
          <span className="text-[#F3EBDD]">{a.subject}<span className="ml-2 text-xs text-[#A8A090]">{AUD[a.audience]} · {new Date(a.created_at).toLocaleDateString()}</span></span>
          <span className="text-xs text-[#A8A090]">{a.status === "sending" ? `Sending ${a.sent}/${a.total}…` : `${a.sent} sent${a.failed ? ` · ${a.failed} failed` : ""}`}</span>
        </div>
      ))}
    </div>
  );
};

export const AnnouncePanel = ({ adminEmail }) => {
  const qc = useQueryClient();
  const { data: counts } = useQuery({ queryKey: ["admin-audience"], queryFn: () => api.get("/admin/announcements/audience").then((r) => r.data) });
  const [form, setForm] = useState({ subject: "", body: "", audience: "test", test_email: adminEmail || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (adminEmail) setForm((f) => ({ ...f, test_email: f.test_email || adminEmail })); }, [adminEmail]);

  const send = async () => {
    const n = counts?.[form.audience];
    if (form.audience !== "test" && !window.confirm(`Send "${form.subject}" to ${n} ${AUD[form.audience].toLowerCase()}? This can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announcements", { ...form, test_email: form.audience === "test" ? form.test_email : null });
      toast.success(data.status === "test_sent" ? `Test sent to ${data.to}` : `Sending to ${data.total} people`);
      if (data.status !== "test_sent") { setForm({ ...form, subject: "", body: "", audience: "test" }); qc.invalidateQueries({ queryKey: ["admin-announcements"] }); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-announce">
      <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#FBBF24]" /><div className="lf-label">Announcement email</div></div>
      <p className="mt-2 text-xs text-[#A8A090]">Goes to confirmed accounts that haven't unsubscribed. Every email has an unsubscribe link. Leave a blank line between paragraphs; https:// links become clickable.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[220px_1fr]">
        <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
          <SelectTrigger data-testid="announce-audience"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(AUD).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`announce-audience-${k}`}>{l}{counts && k !== "test" ? ` (${counts[k]})` : ""}</SelectItem>)}</SelectContent>
        </Select>
        {form.audience === "test"
          ? <Input type="email" value={form.test_email} onChange={(e) => setForm({ ...form, test_email: e.target.value })} placeholder="you@example.com" data-testid="announce-test-email" />
          : <div className="self-center text-xs text-[#A8A090]" data-testid="announce-count">{counts?.[form.audience] ?? "…"} recipients</div>}
      </div>
      <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject, e.g. NaNoWriMo: Pro is free all November" className="mt-3" maxLength={150} data-testid="announce-subject" />
      <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={7} placeholder="Write your message…" className="mt-3" data-testid="announce-body" />
      <div className="mt-3 flex justify-end">
        <Button onClick={send} disabled={busy || !form.subject.trim() || !form.body.trim() || (form.audience === "test" && !form.test_email)} data-testid="announce-send-button">
          <Send className="mr-2 h-4 w-4" />{form.audience === "test" ? "Send test" : "Send announcement"}
        </Button>
      </div>
      <History />
    </section>
  );
};
